/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Text, Title, UnstyledButton } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import { formatStatsCurrency } from '@/shared/utils/format-stats';

import { SaleStateBadge } from './SaleStateBadge';
import { IdentifierChip } from './sale-detail-atoms';
import type { Sale, SalePaperworkMilestoneType } from '../types/sale.types';
import {
  SALE_STATE_ACCENTS,
  isTerminal,
  milestoneLabel,
  nextPendingMilestone,
  useSaleStateLabel,
  type SaleState,
} from '../constants/sale-states';

export interface SaleDetailHeaderProps {
  sale: Sale;
  /** The property's readable headline, already resolved. */
  propertyLabel: string;
  /** Neighbourhood and city, when known. */
  propertyLocality: string;
  onOpenOriginDeal: () => void;
  /** The status band: milestone progress and the actions. */
  band?: ReactNode;
  /** The responsible-broker block, on the right of the identity band. */
  responsible?: ReactNode;
}

/**
 * Identity of the sale: who bought, what state it is in, which record it is,
 * what was sold and for how much.
 *
 * The buyer leads because that is how a broker refers to the sale. The value
 * sits in the same line as the property rather than in the KPI row's place,
 * so the header answers "which sale is this" on its own.
 *
 * `accepted_value` is shown, never edited. The field is editable in the data
 * model, but no HTTP route exposes that, so offering an input here would be a
 * control that silently fails.
 *
 * @param props - The sale, its resolved property, and the origin navigation.
 * @returns The rendered header.
 */
export function SaleDetailHeader({
  sale,
  propertyLabel,
  propertyLocality,
  onOpenOriginDeal,
  band,
  responsible,
}: SaleDetailHeaderProps) {
  const { t } = useTranslation('features/sales');

  // The property titles the screen now, so the subtitle carries who is
  // buying it and where it is — the two facts the title cannot hold.
  const buyerLabel = sale.buyer_name || t('detail.unnamedBuyer');
  const subtitle = [buyerLabel, propertyLocality].filter(Boolean).join(' · ');

  return (
    <div className="overflow-hidden rounded-(--radius-lg) border border-(--border-subtle) bg-(--surface-card)">
      <div className="flex flex-wrap items-start justify-between gap-(--space-5) px-(--space-6) pt-(--space-5) pb-(--space-4)">
        <div className="flex min-w-0 flex-col gap-(--space-1-5)">
      <div className="flex flex-wrap items-center gap-(--space-2)">
        <Title
          order={1}
          // Size and weight go through `style`, not a class: Title resolves its
          // own font size into an inline style, which would override any
          // `text-[length:…]` utility set here and silently ignore it.
          style={{
            fontSize: 'var(--text-app-title)',
            fontWeight: 'var(--font-semibold)',
            margin: 0,
          }}
          className={cn('leading-tight tracking-(--tracking-app-title)')}
          data-testid="sale-detail-title"
        >
          {propertyLabel || t('detail.untitledProperty')}
        </Title>
        <SaleStateBadge state={sale.state} />
        <IdentifierChip title={sale.id}>{shortSaleId(sale.id)}</IdentifierChip>
      </div>

      <div className="flex flex-wrap items-baseline gap-(--space-2)">
        <Text
          component="span"
          variant="bodySm"
          tone="secondary"
          data-testid="sale-detail-property"
        >
          {subtitle}
        </Text>
        <Text
          component="span"
          variant="bodySm"
          fw={600}
          tone="primary"
          className="tabular-nums"
          data-testid="sale-detail-value"
        >
          {formatStatsCurrency(sale.accepted_value)}
        </Text>
      </div>

      {sale.origin_deal_id && (
        <UnstyledButton
          onClick={onOpenOriginDeal}
          data-testid="sale-origin-deal-link"
          className={cn(
            'w-fit cursor-pointer text-(length:--text-small)',
            'text-(--seed-base) hover:underline',
          )}
        >
          {t('detail.originDeal')} ↗
        </UnstyledButton>
      )}
        </div>

        {responsible}
      </div>

      {band}
    </div>
  );
}


/**
 * The header's second band: how far the paperwork has come, and what to do
 * next.
 *
 * A sale has no pipeline — it is `in_paperwork`, `completed` or `voided` —
 * so a stage strip would be inventing a funnel that does not exist. What
 * does progress is the lavratura, one milestone at a time, so the bar has a
 * segment per configured milestone type and fills the ones that carry a
 * date.
 *
 * Nothing here is recomputed. The filled segments read `sale.paperwork`
 * directly, and which milestone is next comes from the backend's
 * `commission_panel.paperwork_pending`, never from a front-end guess about
 * which types are required.
 *
 * @param props.sale - The sale, for its recorded milestone dates.
 * @param props.milestoneTypes - The agency's configured milestone types.
 * @param props.pending - `commission_panel.paperwork_pending`, or undefined
 *   while the panel is still loading.
 * @param props.isLoading - True while the milestone types or panel load.
 * @param props.actions - The action row, absent once the sale is terminal.
 * @returns The status band.
 */
export function SaleStatusBand({
  sale,
  milestoneTypes,
  pending,
  isLoading,
  actions,
}: {
  sale: Sale;
  milestoneTypes: SalePaperworkMilestoneType[];
  pending: string[] | undefined;
  isLoading: boolean;
  actions?: ReactNode;
}) {
  const { t } = useTranslation('features/sales');
  const stateLabel = useSaleStateLabel();

  // Counted over the required types only, because that is the set the
  // backend pends. Counting every configured type instead let the band read
  // "3 de 4 marcos" beside "Papelada completa" — true of two different
  // denominators, and nonsense on one line. Optional milestones still get a
  // segment, they just do not gate completeness.
  const ordered = [...milestoneTypes].sort((a, b) => a.order - b.order);
  const gating = ordered.filter((type) => type.required);
  const recorded = gating.filter((type) => Boolean(sale.paperwork?.[type.id]));
  const next = nextPendingMilestone(pending, milestoneTypes);

  // `nextPendingMilestone` returns null both when nothing is pending and
  // when the types have not arrived yet. Only the first of those means the
  // paperwork is done, so the loading case has to be told apart explicitly
  // or a fresh page reads "complete" for a moment.
  const settled = !isLoading && pending != null;

  // A terminal sale gets no pending hint at all. The backend empties
  // `paperwork_pending` once a sale is completed or voided, so a voided sale
  // with nothing recorded would otherwise read "Desfeita · 0 de 4 marcos ·
  // Completa" — three claims on one line, one of them false. The state label
  // and the outcome card already say what happened.
  const hint = isTerminal(sale.state)
    ? ''
    : !settled
      ? t('detail.kpiCards.paperworkLoading')
      : next
        ? t('detail.kpiCards.paperworkNextHint', { milestone: milestoneLabel(next, t) })
        : t('detail.kpiCards.paperworkComplete');

  return (
    <div
      data-testid="sale-status-band"
      className={cn(
        'flex flex-wrap items-center gap-(--space-4) border-t border-(--border-subtle)',
        // The page ground, like the property detail's PriceStrip and the
        // attendance's band. `--surface-tertiary` is a well tier, so the
        // band read as a tray bolted under the header rather than part of it.
        'bg-(--surface-primary) px-(--space-6) py-(--space-3-5)',
      )}
    >
      <div className="flex min-w-[250px] flex-1 flex-col gap-(--space-1-5)">
        <div className="flex flex-wrap items-baseline gap-(--space-2)">
          <Text
            component="span"
            style={{ color: SALE_STATE_ACCENTS[sale.state as SaleState] }}
            className="text-[length:var(--text-app-section)] [font-weight:var(--font-semibold)] leading-tight"
          >
            {stateLabel(sale.state)}
          </Text>
          {gating.length > 0 && (
            <Text component="span" variant="meta" tone="tertiary">
              {t('detail.kpiCards.paperworkProgress', {
                done: recorded.length,
                total: gating.length,
              })}
            </Text>
          )}
        </div>
        {ordered.length > 0 && (
          <div className="flex items-center gap-(--space-1)">
            {ordered.map((type) => (
              <div
                key={type.id}
                title={milestoneLabel(type, t)}
                className="h-1.5 flex-1 rounded-full"
                style={{
                  background: sale.paperwork?.[type.id]
                    ? 'var(--accent-mostarda)'
                    : 'var(--border-subtle)',
                }}
              />
            ))}
          </div>
        )}
        {hint && (
          <Text component="span" variant="meta" tone="secondary">
            {hint}
          </Text>
        )}
      </div>

      {actions && <div className="ml-auto flex flex-wrap justify-end gap-(--space-2)">{actions}</div>}
    </div>
  );
}

/**
 * Shortens a sale's UUID to its first segment for display.
 *
 * The record has no human-facing reference number, only a UUID. Printing all
 * 36 characters in a chip crowds the header out, so the first segment is
 * shown and the full id kept in the element's title for copying.
 *
 * @param id - The sale's full identifier.
 * @returns The leading segment, uppercased.
 */
function shortSaleId(id: string): string {
  return (id.split('-')[0] || id).toUpperCase();
}

export default SaleDetailHeader;
